import React, {useEffect, useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import * as Yup from "yup";
import {defaultVehicle} from "../generic/InitialStates";
import {AGCText} from "../../generic-components/AGCText";
import {apiGenericCreate, apiGenericUpdate, genericGetOne, getAllOrgSettings} from "../generic/crud";
import {ENTITY_ID_VEHICLE} from "../generic/Constants";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../../utils/toast/Toast";
import {useVehicleCapture} from "../../hooks/useVehicleCapture";
import {WeighbridgeCaptureModal} from "../../components/weighbridge/WeighbridgeCaptureModal";

export function VehicleAddEditDialog(props) {
    const {show, handleClose, id} = props;
    const [objectForSubmit, setObjectForSubmit] = useState(defaultVehicle);
    const [isLoading, setIsLoading] = useState(false);
    const [forUpdate, setForUpdate] = useState(false);
    const [title, setTitle] = useState("Vehicle");

    const { capture, isCapturing, weight: weightTrack, image: imageTrack, groupSelectModal } = useVehicleCapture(show);
    const [weighbridgeEnabled, setWeighbridgeEnabled] = useState(true);
    const [allowManualEmptyWeight, setAllowManualEmptyWeight] = useState(false);

    const entityName = "Vehicle";

    // Load org settings to check if weighbridge capture is enabled and manual empty weight is allowed
    useEffect(() => {
        if (show) {
            getAllOrgSettings()
                .then((response) => {
                    const settings = response.data || [];
                    const wbSetting = settings.find(s => s.typeName === 'ENABLE_WEIGHBRIDGE_CAPTURE');
                    if (wbSetting) {
                        setWeighbridgeEnabled(wbSetting.value === true || wbSetting.value === 'true');
                    }
                    const manualSetting = settings.find(s => s.typeName === 'ALLOW_MANUAL_EMPTY_WEIGHT');
                    if (manualSetting) {
                        setAllowManualEmptyWeight(manualSetting.value === true || manualSetting.value === 'true');
                    }
                })
                .catch(() => {});
        }
    }, [show]);

    const validationSchema = Yup.object({
        vehicleNumber: Yup.string().required("Vehicle Number is required"),
        emptyWeight: Yup.string().required("Empty Weight is required"),
        modelMake: Yup.string()
    });

    const formik = useFormik({
        initialValues: objectForSubmit,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit(vehicle, {resetForm}) {
            setIsLoading(true);

            if (!id) {
                apiGenericCreate(ENTITY_ID_VEHICLE, vehicle).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Created!", TOAST_SUCCESS);
                    resetForm();
                }).catch((error) => {
                    setIsLoading(false);
                    // The global axios interceptor already toasts the server's message
                    // (e.g. a duplicate vehicle number). Only speak up when it stayed silent.
                    if (error.response && !error.response.data) {
                        showToast("Error creating " + entityName + ". Please try again.", TOAST_FAILURE);
                    }
                });
            } else {
                apiGenericUpdate(ENTITY_ID_VEHICLE, vehicle, id).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Updated!", TOAST_SUCCESS);
                    resetForm();
                }).catch((error) => {
                    setIsLoading(false);
                    // The global axios interceptor already toasts the server's message
                    // (e.g. a duplicate vehicle number). Only speak up when it stayed silent.
                    if (error.response && !error.response.data) {
                        showToast("Error updating " + entityName + ". Please try again.", TOAST_FAILURE);
                    }
                });
            }
        }
    });

    const handleCaptureWeight = async () => {
        const res = await capture({ weight: true });
        if (res.cancelled) return;
        if (res.weight != null) {
            formik.setFieldValue("emptyWeight", res.weight);
            showToast("Weight captured: " + res.weight, TOAST_SUCCESS);
        } else {
            showToast(res.weightError?.message || "Failed to capture weight", TOAST_FAILURE);
        }
    };

    useEffect(() => {
        if (id != null) {
            setForUpdate(true);
            setTitle("Edit " + entityName);
            setIsLoading(true);
            genericGetOne(ENTITY_ID_VEHICLE, id).then(
                ({data}) => {
                    setObjectForSubmit(data);
                    setTitle("Update " + entityName);
                    setIsLoading(false);
                })
                .catch((error) => {
                    setIsLoading(false);
                    showToast(
                        "Error fetching " + entityName + " data: " + (error.message || "Unknown error"),
                        TOAST_FAILURE
                    );
                });
        } else {
            setForUpdate(false);
            setObjectForSubmit(defaultVehicle);
            setTitle("New " + entityName);
        }
    }, [show]);

    return (
        <>
        <Modal
            size="lg"
            show={show}
            onHide={handleClose}
            aria-labelledby="example-modal-sizes-title-lg">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay">
                {!isLoading ? (
                    <Form className="form form-label-right" onSubmit={formik.handleSubmit}>
                        {/* Vehicle Number - read-only when editing */}
                        <div className="form-group row mb-5">
                            <Form.Group className="col-lg-6">
                                <AGCText formik={formik}
                                         name={"Vehicle Number"}
                                         placeHolder={'Enter Vehicle Number'}
                                         id={"vehicleNumber"}
                                         toolTip={"Please enter the Vehicle Number"}
                                         value={formik.values.vehicleNumber}
                                         readOnly={forUpdate}
                                         required={true}/>
                                {forUpdate && (
                                    <small className="text-muted">
                                        <i className="fas fa-lock me-1 fs-9"></i>
                                        Vehicle number cannot be changed
                                    </small>
                                )}
                            </Form.Group>

                            {/* Model/Make field */}
                            <Form.Group className="col-lg-6">
                                <AGCText formik={formik}
                                         name={"Model/Make"}
                                         placeHolder={'Enter Model/Make (optional)'}
                                         id={"modelMake"}
                                         toolTip={"Optional: Vehicle Model/Make information"}
                                         value={formik.values.modelMake}
                                         required={false}/>
                            </Form.Group>
                        </div>

                        {/* Empty Weight */}
                        <div className="form-group row mb-5">
                            <Form.Group className={weighbridgeEnabled ? "col-lg-6" : "col-lg-12"}>
                                <AGCText formik={formik}
                                         name={"Empty Weight (Tons)"}
                                         placeHolder={weighbridgeEnabled && !allowManualEmptyWeight ? 'Use Capture Weight button' : 'Enter Empty Weight'}
                                         id={"emptyWeight"}
                                         toolTip={weighbridgeEnabled && !allowManualEmptyWeight ? "Capture the empty weight from the weighing scale" : "Enter the empty weight in tons"}
                                         value={formik.values.emptyWeight}
                                         readOnly={weighbridgeEnabled && !allowManualEmptyWeight}
                                         required={true}/>
                                {weighbridgeEnabled && !allowManualEmptyWeight && (
                                    <small className="text-muted">
                                        <i className="fas fa-info-circle me-1 fs-9"></i>
                                        Weight can only be set via the weighing scale
                                    </small>
                                )}
                            </Form.Group>

                            {weighbridgeEnabled && (
                                <Form.Group className="col-lg-6 d-flex align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-info btn-sm"
                                        onClick={handleCaptureWeight}
                                        disabled={isCapturing}>
                                        {isCapturing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Capturing...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-weight-hanging me-2"></i>
                                                Capture Weight
                                            </>
                                        )}
                                    </button>
                                </Form.Group>
                            )}
                        </div>

                        <Modal.Footer>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-light btn-elevate"
                                    onClick={handleClose}>
                                    Cancel
                                </button>
                                <> </>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-elevate">
                                    Save
                                </button>
                            </div>
                        </Modal.Footer>
                    </Form>
                ) : (
                    <div className="text-center p-4">
                        <span>Please Wait, Fetching Information from Server</span>
                    </div>
                )}
            </Modal.Body>
        </Modal>

        {/* Weight Capture Processing Modal */}
        <WeighbridgeCaptureModal show={isCapturing} weight={weightTrack} image={imageTrack} />
        {groupSelectModal}
        </>
    );
}
